import type { GameAction, GamePhase, PlayerObservationV1 } from "@fate-kingdom/engine";

import type { AlphaTutorialConcept, AlphaTutorialState } from "./alpha-tutorial-types";

export interface AlphaObjectiveRecommendation {
  readonly kind: "required" | "recommended" | "informational";
  readonly message: string;
  readonly highlight:
    "phase" | "land" | "lane" | "commander" | "army" | "battle" | "confirm" | "end" | null;
}

function hasAction(actions: readonly GameAction[], type: GameAction["type"]): boolean {
  return actions.some((action) => action.type === type);
}

function visibleAttack(
  observation: PlayerObservationV1,
  laneId: "left" | "center" | "right",
): number {
  const side = observation.lanes[laneId].own;
  return side.commander === null
    ? 0
    : (side.commander.attack ?? 0) +
        side.armies.reduce((total, army) => total + army.attack, 0) +
        (side.land?.attackBonus ?? 0);
}

function visibleGuard(
  observation: PlayerObservationV1,
  laneId: "left" | "center" | "right",
): number {
  const side = observation.lanes[laneId].opponent;
  return (
    (side.commander?.guard ?? 0) +
    side.armies.reduce((total, army) => total + army.guard, 0) +
    (side.land?.guardBonus ?? 0)
  );
}

export function getAlphaObjectiveRecommendation({
  observation,
  legalActions,
}: {
  readonly observation: PlayerObservationV1;
  readonly legalActions: readonly GameAction[];
}): AlphaObjectiveRecommendation {
  if (observation.status.type === "finished") {
    return {
      kind: "informational",
      message: "The match is complete. Claim two lanes to win a match.",
      highlight: null,
    };
  }
  if (observation.activePlayerId !== observation.perspectivePlayerId) {
    return {
      kind: "informational",
      message: "The AI is taking its turn. Your hand stays visible but cannot be played yet.",
      highlight: null,
    };
  }
  switch (observation.phase) {
    case "ready":
      return {
        kind: "recommended",
        message: "Ready resolves automatically when there is no choice.",
        highlight: "phase",
      };
    case "draw":
      return {
        kind: "recommended",
        message: "Draw resolves automatically when there is no choice.",
        highlight: "phase",
      };
    case "action-window": {
      const positiveDamage = legalActions.some(
        (action) =>
          action.type === "attack" &&
          visibleAttack(observation, action.laneId) > visibleGuard(observation, action.laneId),
      );
      if (hasAction(legalActions, "found-land") || hasAction(legalActions, "develop-land")) {
        return {
          kind: "recommended",
          message: "Play a Land, deploy a formation, attack, or play an Event in any useful order.",
          highlight: "land",
        };
      }
      if (
        hasAction(legalActions, "deploy-commander") ||
        hasAction(legalActions, "replace-commander") ||
        hasAction(legalActions, "deploy-army")
      ) {
        return {
          kind: "recommended",
          message: "Deploy a Commander or Army now, or attack first and deploy afterward.",
          highlight: "commander",
        };
      }
      return positiveDamage
        ? {
            kind: "recommended",
            message: "Attack a vulnerable lane, or End Turn when you are finished.",
            highlight: "battle",
          }
        : {
            kind: "recommended",
            message: "No clearly positive action remains. End Turn when ready.",
            highlight: "end",
          };
    }
    case "land":
      if (hasAction(legalActions, "found-land")) {
        return {
          kind: "recommended",
          message: "Drag a Land card onto a glowing empty lane.",
          highlight: "land",
        };
      }
      return {
        kind: "recommended",
        message: "Drag a matching Land onto a glowing developed lane.",
        highlight: "land",
      };
    case "supply":
      if (hasAction(legalActions, "discard-army-for-upkeep")) {
        return {
          kind: "required",
          message: "Your local Supply is short. Choose one highlighted Army to remove.",
          highlight: "confirm",
        };
      }
      return {
        kind: "recommended",
        message: "Supply will continue automatically when no Army must be removed.",
        highlight: "phase",
      };
    case "command":
      if (
        hasAction(legalActions, "deploy-commander") ||
        hasAction(legalActions, "replace-commander")
      ) {
        return {
          kind: "recommended",
          message: "Drag a Commander onto one of your Lands.",
          highlight: "commander",
        };
      }
      if (hasAction(legalActions, "deploy-army")) {
        return {
          kind: "recommended",
          message: "Drag an Army onto any active friendly Land.",
          highlight: "army",
        };
      }
      return {
        kind: "recommended",
        message:
          observation.own.actionsUsed.deploymentActions > 0
            ? "Your Deployment Action is used. Armies can be deployed on a later turn."
            : "You can continue to Battle when you are ready.",
        highlight: "end",
      };
    case "battle": {
      const positiveDamage = legalActions.some(
        (action) =>
          action.type === "attack" &&
          visibleAttack(observation, action.laneId) > visibleGuard(observation, action.laneId),
      );
      return positiveDamage
        ? {
            kind: "recommended",
            message: "Attack a lane where your visible Attack exceeds the opposing Guard.",
            highlight: "battle",
          }
        : {
            kind: "recommended",
            message: "No attack currently deals visible damage. You may end the Battle Phase.",
            highlight: "end",
          };
    }
    case "cleanup":
      return {
        kind: "recommended",
        message: "End your turn. Remaining hand cards and unused Pool will clear.",
        highlight: "end",
      };
    case "finished":
      return { kind: "informational", message: "The match is complete.", highlight: null };
  }
}

export function getTutorialConceptsForAcceptedAction(
  action: GameAction,
  phaseBeforeAction: GamePhase,
): readonly AlphaTutorialConcept[] {
  switch (action.type) {
    case "found-land":
    case "develop-land":
      return ["land"];
    case "deploy-commander":
    case "replace-commander":
      return ["commander"];
    case "deploy-army":
      return ["army"];
    case "attack":
      return ["battle"];
    case "advance-phase":
      if (phaseBeforeAction === "supply") return ["supply"];
      if (phaseBeforeAction === "cleanup") return ["cleanup"];
      return [];
    case "end-turn":
      return ["cleanup"];
    default:
      return [];
  }
}

export function getTutorialHighlight(
  tutorial: Pick<AlphaTutorialState, "enabled" | "completedConcepts" | "dismissedCurrentHint">,
  recommendation: AlphaObjectiveRecommendation,
): AlphaObjectiveRecommendation["highlight"] {
  if (!tutorial.enabled || tutorial.dismissedCurrentHint) return null;
  return recommendation.highlight;
}

export const ALPHA_PHASE_COPY: Readonly<
  Record<GamePhase, { readonly title: string; readonly description: string }>
> = {
  ready: { title: "Ready", description: "Your existing Commanders become ready." },
  draw: { title: "Draw", description: "Draw cards. Each player skips their first Draw once." },
  land: { title: "Land", description: "Found an empty lane or develop one existing Land." },
  supply: {
    title: "Supply",
    description: "Lands produce resources and local Armies require Upkeep.",
  },
  command: { title: "Command", description: "Deploy or manage Commanders and Armies." },
  battle: { title: "Battle", description: "Attack the opposing Land from a matching lane." },
  cleanup: {
    title: "Cleanup",
    description: "Hand cards and unused resources clear before the turn passes.",
  },
  "action-window": {
    title: "Your Action Window",
    description: "Play cards, attack, disband, and use Events in the order you choose.",
  },
  finished: { title: "Complete", description: "The match has ended." },
};
